// The address, as a value (ADR-0009). Pure: a string in, a `ViewState` out, and
// back again. Nothing here reads `location`, `history` or a clock — that is the
// store's half, in `src/state/viewstate.ts`, which is this module's only
// caller for anything but the types.
//
// Split out when `uklad` arrived (SPEC-viewfinder §2.3): the store had grown to
// the 400-line limit, and the codec is the half that a new key touches. The
// types live here rather than in the store because they are what the codec
// produces; the store re-exports them, so every reader still says
// `from 'state/viewstate.ts'`.
//
// **Ten keys, four of them remembered** — see ADR-0009 §Amendments.
import type { Voice } from '../shared/model.ts';

export type Screen = 'linie' | 'warstwy' | 'pomoc';

/**
 * `uklad` — which value the rail is ordered by (§2.3). Time is the default and
 * is never switched automatically: „Poznań teraz to jednak w pierwszej
 * kolejności czas". The values are the resident's own words, like `Screen`'s.
 */
export type Order = 'czas' | 'odleglosc';

export type Centre =
  | { kind: 'default' } // Stary Rynek
  | { kind: 'me' } // geolocation granted
  | { kind: 'picked'; lat: number; lon: number; name: string };

export type ViewState = {
  /** `linie` — the resident's lines, in the order they were added. */
  lines: string[];
  /** `cisza` — muted voices. A muted voice is a switched-off layer; see §5.7. */
  muted: Voice[];
  /** `solo` — at most one voice. Mutually exclusive with `muted` being meaningful. */
  solo: Voice | null;
  /** `tylko` — the line lens. `#tylko=160` from today's links parses into this. */
  onlyLine: string | null;
  /** `r` — radius in metres, 200…5000, rounded to 10 m. */
  radius: number;
  /** `tu` — serialised as `lat,lon,name`; absent means `{ kind: 'default' }`. */
  centre: Centre;
  /** `teraz` — `HH:MM` local; null means "now", and the clock keeps running. */
  at: string | null;
  /** `sel` — the open item's id (§4.4), or null. */
  selected: string | null;
  /** `ekran` — the open overlay, or null. */
  screen: Screen | null;
  /** `uklad` — the rail's order (§2.3). `'czas'` is the default and is omitted. */
  order: Order;
};

const VOICES = ['ztm', 'ztmk', 'pl', 'aq'] as const satisfies readonly Voice[];
const SCREENS = ['linie', 'warstwy', 'pomoc'] as const satisfies readonly Screen[];
const ORDERS = ['czas', 'odleglosc'] as const satisfies readonly Order[];

/** The radius the prototype opens on: 3,0 km. */
const DEFAULT_RADIUS = 3000;
const MIN_RADIUS = 200;
const MAX_RADIUS = 5000;
/** The grain `r` is held to, so an address is short and two devices agree (ADR-0009). */
const RADIUS_STEP = 10;

/** The order the rail opens in, and the one value `uklad` never spells. */
const DEFAULT_ORDER: Order = 'czas';

/**
 * `tu=ja` is the centre following the resident. A shared link carrying it never
 * re-asks the browser at boot — the permission prompt belongs to the tap on
 * „tu" — so it resolves silently when permission is already granted and stays
 * Stary Rynek otherwise, with the note that says so.
 */
const ME = 'ja';

export const DEFAULT_VIEW: ViewState = {
  lines: [],
  muted: [],
  solo: null,
  onlyLine: null,
  radius: DEFAULT_RADIUS,
  centre: { kind: 'default' },
  at: null,
  selected: null,
  screen: null,
  order: DEFAULT_ORDER,
};

function voices(raw: string | null): Voice[] {
  if (!raw) return [];
  const named = raw.split(',').filter((v): v is Voice => VOICES.some((known) => known === v));
  return [...new Set(named)];
}

function radius(raw: string | null): number {
  const n = Number(raw);
  if (!raw || !Number.isFinite(n)) return DEFAULT_RADIUS;
  return Math.round(Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, n)) / RADIUS_STEP) * RADIUS_STEP;
}

/**
 * The smallest radius this app can carry that still reaches `metres` — the one
 * rule behind both places that widen the ring for something outside it: the
 * empty-ring sentence (§2.6) and a `sel=` naming an item beyond the radius
 * (§6, #225). Held to the same 10 m grain and the same 5 km ceiling `r` itself
 * has, and `null` past the ceiling, where the answer is to pan instead.
 */
export function radiusIncluding(metres: number): number | null {
  const step = Math.ceil(Math.max(MIN_RADIUS, metres) / RADIUS_STEP) * RADIUS_STEP;
  return step > MAX_RADIUS ? null : step;
}

function centre(raw: string | null): Centre {
  if (!raw) return { kind: 'default' };
  if (raw === ME) return { kind: 'me' };
  // `lat,lon,name`, and the name may itself contain commas
  const [lat, lon, ...rest] = raw.split(',');
  const name = rest.join(',').trim();
  const [a, b] = [Number(lat), Number(lon)];
  if (!name || !Number.isFinite(a) || !Number.isFinite(b)) return { kind: 'default' };
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return { kind: 'default' };
  return { kind: 'picked', lat: a, lon: b, name };
}

function clock(raw: string | null): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(raw ?? '');
  if (!m) return null;
  const [h, min] = [Number(m[1]), Number(m[2])];
  return h < 24 && min < 60 ? raw : null;
}

/** Pure. Unknown keys are dropped, malformed values fall back to the default. */
export function parseHash(hash: string): ViewState {
  const q = new URLSearchParams(hash.replace(/^#/, ''));
  const lines = (q.get('linie') ?? '')
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean);
  const solo = voices(q.get('solo'))[0] ?? null;
  return {
    lines: [...new Set(lines)],
    muted: voices(q.get('cisza')),
    solo,
    onlyLine: q.get('tylko')?.trim() || null,
    radius: radius(q.get('r')),
    centre: centre(q.get('tu')),
    at: clock(q.get('teraz')),
    selected: q.get('sel')?.trim() || null,
    screen: SCREENS.find((s) => s === q.get('ekran')) ?? null,
    // A value this app does not know is not an order, and the rail has to be in
    // one: `uklad=blisko` from a typo or an older link reads as the default.
    order: ORDERS.find((o) => o === q.get('uklad')) ?? DEFAULT_ORDER,
  };
}

function centreParam(c: Centre): string | null {
  if (c.kind === 'default') return null;
  if (c.kind === 'me') return ME;
  return `${c.lat},${c.lon},${c.name}`;
}

/** Pure. Stable key order; a field equal to its default is omitted. */
export function toHash(v: ViewState): string {
  const parts: [string, string | null][] = [
    ['linie', v.lines.length ? v.lines.join(',') : null],
    ['cisza', v.muted.length ? v.muted.join(',') : null],
    ['solo', v.solo],
    ['tylko', v.onlyLine],
    ['r', v.radius === DEFAULT_RADIUS ? null : String(v.radius)],
    ['tu', centreParam(v.centre)],
    ['teraz', v.at],
    ['sel', v.selected],
    ['ekran', v.screen],
    ['uklad', v.order === DEFAULT_ORDER ? null : v.order],
  ];
  const q = new URLSearchParams();
  for (const [key, value] of parts) if (value !== null) q.set(key, value);
  // URLSearchParams percent-encodes the comma, which turns a readable address
  // into a wall of %2C; the separator is ours and safe to leave alone.
  return q.toString().replaceAll('%2C', ',');
}
